import { MouseEvent, ReactElement, useState } from "react";
import { RelativeRoutingType, useLocation, useMatch, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
/* eslint-enable import/no-duplicates */
import { Folder, InsertDriveFileOutlined, MoreHoriz } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  Menu,
  MenuItem,
  menuItemClasses,
  MenuItemProps,
  Snackbar,
  Typography as T,
} from "@mui/material";
import { TypographyProps } from "@mui/material/Typography/Typography";
/* eslint-disable import/no-duplicates */
import { formatDistanceStrict, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import DeleteFileDialog from "files/components/DeleteFileDialog";
import FileActionDialog from "files/components/FileActionDialog";
import FileDownload from "files/components/FileDownload";
import FileTitle from "files/components/FileTitle";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import { BASE_DIRECTORIES, encodeObjectKey, resolveRouterPath } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import { JobType } from "files/types/interfaces/fileJob";
import prettyBytes from "pretty-bytes";
import { LinkListItem } from "~/components/ListCard";
import TextOverflow from "~/components/TextOverflow";
import rumClient from "~/utils/monitoring";

interface FileRowProps {
  fileKey: string;
  hideActions?: boolean;
  type?: FileType;
  subtitle?: string;
  size?: number;
  updatedAt?: string;
  pendingAction?: JobType;
  badge?: ReactElement;
  s3Uri?: string;
  disabled?: boolean;
}

/**
 * Overrides for default date-fns relative date distance formatting
 */
const formatDistanceLocale: Record<string, { one: string; other: string }> = {
  xSeconds: { one: "now", other: "now" },
  xDays: {
    one: "yesterday",
    other: "{{count}} days ago",
  },
  xWeeks: {
    one: "last week",
    other: "{{count}} weeks ago",
  },
  xMonths: {
    one: "last month",
    other: "{{count}} months ago",
  },
  xYears: {
    one: "last year",
    other: "{{count}} years ago",
  },
};

/**
 * Overrides behavior of https://github.com/date-fns/date-fns/blob/main/src/locale/en-US/_lib/formatDistance/index.ts
 * Falls back to default result if token not found in overrides above
 */
const formatDistance = (token: string, count: number) => {
  const tokenValue = formatDistanceLocale[token];

  if (tokenValue) {
    if (count === 1) {
      return tokenValue.one;
    } else {
      return tokenValue.other.replace("{{count}}", count.toString());
    }
  }

  return enUS.formatDistance?.(token, count, { addSuffix: true });
};

export const formatRelativeDate = (iso: string) => {
  try {
    const today = new Date();
    const parsedDate = parseISO(iso);
    return formatDistanceStrict(parsedDate, today, { locale: { ...enUS, formatDistance } });
  } catch (e) {
    console.error(e);
    rumClient?.recordError(e);
  }
  return "";
};

const FileMetadata = ({ sx, children, ...rest }: TypographyProps) => (
  <T variant="body2" sx={{ opacity: 0.7, textWrap: "nowrap", ...sx }} {...rest}>
    {children}
  </T>
);

const PendingActionLabel = ({ pendingAction }: { pendingAction: JobType }) => {
  const labels: Record<JobType, string> = {
    [JobType.MOVE]: "Moving",
    [JobType.RENAME]: "Renaming",
    [JobType.COPY]: "Copying",
  };

  return (
    <T variant="body2" sx={{ fontSize: 14, fontStyle: "italic", opacity: 0.7, textWrap: "nowrap" }}>
      {labels[pendingAction]}
    </T>
  );
};

const ActionMenuItem = ({
  onClick,
  ...rest
}: MenuItemProps & { onClick: (e: MouseEvent) => any }) => {
  return (
    <MenuItem
      onClick={(e) => {
        // Avoid navigating to file
        e?.stopPropagation();
        onClick(e);
      }}
      {...rest}
    />
  );
};

const FileRow = ({
  subtitle,
  fileKey,
  type,
  size,
  pendingAction,
  updatedAt,
  hideActions,
  badge,
  s3Uri,
  disabled = false,
}: FileRowProps) => {
  const [, copy] = useCopyToClipboard();
  const location = useLocation();
  const { datasetId } = useParams();
  const basePath = useBasePath();
  const isGlobalFileContext = !useMatch("/workbenches/:workbenchId/files/*");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const canModify = useFileAccess(fileKey, "write");
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const isFile = type === FileType.FILE;
  const routerPath = encodeObjectKey(resolveRouterPath(fileKey, isGlobalFileContext, datasetId));
  const fileId = routerPath.toLowerCase().replace("/", "-");
  const sizeLabel = isFile && typeof size === "number" ? prettyBytes(size) : null;
  const showActions =
    !pendingAction &&
    !hideActions && // Disable for back nav (..)
    !BASE_DIRECTORIES[routerPath] && // Disable for top level virtual directories
    !new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/$`).test(fileKey); // Disable for top of project directories

  const handleActionMenuClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCopyLink = () => {
    // Add preview qs for files (see `useFiles` hook for more info on loading patterns)
    const qs = type === FileType.FILE ? "?preview=1" : "";
    const url = new URL(`${basePath}/${routerPath}${qs}`, window.location.origin);
    setAnchorEl(null);
    setSuccessMessage("Link copied to clipboard");
    copy(url.toString());
  };

  const handleCopyS3Uri = () => {
    if (s3Uri) {
      setAnchorEl(null);
      copy(s3Uri);
      setSuccessMessage("Cloud path copied to clipboard");
    }
  };

  const handleDelete = () => {
    setAnchorEl(null);
    setDeleteModalOpen(true);
  };

  const linkProps = isFile
    ? {
        // File preview stays in current place, updating
        // router state with file key and size information
        to: { pathname: ".", search: location.search },
        navOptions: {
          replace: true,
          relative: "path" as RelativeRoutingType,
          state: { preview: { key: fileKey, size } },
        },
      }
    : {
        to: routerPath,
        navOptions: {
          relative: (fileKey.startsWith("..") ? "path" : "route") as RelativeRoutingType,
        },
      };

  return (
    <>
      <LinkListItem
        {...linkProps}
        disabled={!!pendingAction || disabled}
        icon={
          isFile ? (
            <InsertDriveFileOutlined
              fontSize="small"
              sx={{ color: "primary.dark", opacity: 0.6 }}
            />
          ) : (
            <Folder fontSize="small" sx={{ color: "#a98ff1" }} />
          )
        }
      >
        <Box
          display="flex"
          alignItems="center"
          gap={4}
          height={subtitle ? 36 : 22}
          fontSize={14}
          width="100%"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <TextOverflow autoTooltip variant="subtitle1" fontSize={14} fontWeight={400}>
              <FileTitle fileKey={fileKey} />
            </TextOverflow>
            {subtitle && (
              <TextOverflow
                autoTooltip
                variant="subtitle2"
                color="grey.600"
                fontSize={12}
                fontWeight={400}
              >
                {subtitle}
              </TextOverflow>
            )}
          </Box>

          <FileMetadata minWidth={75} textAlign="end">
            {sizeLabel}
          </FileMetadata>
          <FileMetadata minWidth={125}>{updatedAt && formatRelativeDate(updatedAt)}</FileMetadata>
          <Box minWidth={80}>
            {pendingAction && <PendingActionLabel pendingAction={pendingAction} />}
            {showActions && (
              <Box display="flex" justifyContent="end">
                <IconButton
                  id={`action-btn-${fileId}`}
                  aria-haspopup="true"
                  aria-expanded={anchorEl ? "true" : undefined}
                  aria-controls={anchorEl ? `action-menu-${fileId}` : undefined}
                  onClick={handleActionMenuClick}
                  size="small"
                  sx={{
                    p: 0.5,
                    opacity: 0,
                    transition: `opacity 150ms ease-in-out`,
                    "&[aria-expanded='true']": { opacity: 1 },
                  }}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
                {/* TODO: Should this be replaced by ~/components/ActionsMenu? */}
                <Menu
                  open={!!anchorEl}
                  anchorEl={anchorEl}
                  onClose={(e: Event) => {
                    e.stopPropagation();
                    setAnchorEl(null);
                  }}
                  id={`action-menu-${fileId}`}
                  MenuListProps={{
                    "aria-labelledby": `action-btn-${fileId}`,
                  }}
                  sx={{ mt: 1 }}
                >
                  {isFile && (
                    <FileDownload
                      fileKey={fileKey}
                      callToAction={
                        <ActionMenuItem onClick={() => setAnchorEl(null)}>Download</ActionMenuItem>
                      }
                    />
                  )}
                  <ActionMenuItem onClick={handleCopyLink}>Copy Link</ActionMenuItem>
                  {s3Uri && (
                    <ActionMenuItem onClick={handleCopyS3Uri}>Copy Cloud Path</ActionMenuItem>
                  )}
                  <ActionMenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      setFileAction(JobType.COPY);
                    }}
                  >
                    Copy {type === FileType.DIRECTORY ? "Folder" : "File"}...
                  </ActionMenuItem>
                  {canModify && (
                    <ActionMenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setFileAction(JobType.RENAME);
                      }}
                    >
                      Rename...
                    </ActionMenuItem>
                  )}
                  {canModify && (
                    <ActionMenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setFileAction(JobType.MOVE);
                      }}
                    >
                      Move...
                    </ActionMenuItem>
                  )}
                  {canModify && (
                    <ActionMenuItem
                      onClick={handleDelete}
                      sx={{
                        color: "attention.main",
                        [`&.${menuItemClasses.root}:hover`]: {
                          color: "common.white",
                          bgcolor: "attention.main",
                        },
                      }}
                    >
                      Delete
                    </ActionMenuItem>
                  )}
                </Menu>
              </Box>
            )}
          </Box>
          {badge && (
            <Box display="flex" width={24}>
              {badge}
            </Box>
          )}
        </Box>
      </LinkListItem>

      {fileAction && (
        <FileActionDialog
          fileKey={fileKey}
          open={!!fileAction}
          action={fileAction}
          isDirectory={type === FileType.DIRECTORY}
          handleClose={() => setFileAction(null)}
        />
      )}

      <DeleteFileDialog
        fileKey={fileKey}
        open={deleteModalOpen}
        isDirectory={type === FileType.DIRECTORY}
        handleClose={() => setDeleteModalOpen(false)}
      />
      <Snackbar
        open={!!successMessage}
        autoHideDuration={2000}
        onClose={() => setSuccessMessage("")}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </>
  );
};

export default FileRow;
